import {
  Box,
  Typography,
  Card,
  Chip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon,
  Gavel as CaseIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  PriorityHigh as PriorityIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import RichTextEditor from '../components/RichTextEditor';
import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { STATUS_COLORS } from '../utils/taskColors';

type Task = {
  id: string;
  title: string;
  description: string;
  status: string;
  status_display: string;
  priority: string;
  priority_display: string;
  due_date: string | null;
  is_overdue: boolean;
  assigned_to: string;
  assigned_to_name: string;
  created_by: string;
  created_by_name: string;
  case: string | null;
  case_name: string | null;
  created_at: string;
};

type TaskDetail = Task & {
  attachments?: Array<{
    id: string;
    original_filename: string;
    file_size: number;
    file_url: string;
  }>;
  has_attachments?: boolean;
};

// Helper function to format relative time
const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins === 1) return '1 minute ago';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};

export default function TaskList() {
  const { access } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${access}` }), [access]);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // Default to all tasks
  const [tabValue, setTabValue] = useState(0); // 0 = Tasks, 1 = Tasks I Assigned
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);
  const [editedStatus, setEditedStatus] = useState<string>('');
  const [editedPriority, setEditedPriority] = useState<string>('');
  const [editedDueDate, setEditedDueDate] = useState<string>('');
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [editedDescription, setEditedDescription] = useState<string>('');
  const [editedFiles, setEditedFiles] = useState<File[]>([]);
  const [updating, setUpdating] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<{ element: HTMLElement; task: Task } | null>(null);

  // Create task modal state
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [newTaskAssignedTo, setNewTaskAssignedTo] = useState('');
  const [newTaskFiles, setNewTaskFiles] = useState<File[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);

  useEffect(() => {
    fetchTasks();
  }, [tabValue, statusFilter]);

  // Refetch tasks when window regains focus (to catch updates from other pages)
  useEffect(() => {
    const handleFocus = () => {
      fetchTasks();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [tabValue, statusFilter]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      let endpoint = '/tasks/';
      const params = new URLSearchParams();

      if (tabValue === 0) {
        // My tasks (assigned to me)
        endpoint = '/tasks/my-tasks/';
      } else {
        // Tasks I created/assigned to others
        params.append('created_by_me', 'true');
      }

      // Add status filter
      if (statusFilter === 'not_completed') {
        params.append('exclude_completed', 'true');
      } else if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const queryString = params.toString();
      const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;

      const response = await api.get(fullEndpoint, { headers });
      setTasks(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = useMemo(() => {
    if (!searchTerm) return tasks;

    const lowerSearch = searchTerm.toLowerCase();
    return tasks.filter(
      (task) =>
        task.title.toLowerCase().includes(lowerSearch) ||
        task.description?.toLowerCase().includes(lowerSearch) ||
        task.case_name?.toLowerCase().includes(lowerSearch) ||
        task.assigned_to_name?.toLowerCase().includes(lowerSearch)
    );
  }, [tasks, searchTerm]);

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Task',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', py: 1.5, minHeight: 68, width: '100%', minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.4,
              width: '100%',
            }}
          >
            {params.row.title}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'case_name',
      headerName: 'Case',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%', minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {params.row.case_name || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.row.status_display}
            size="small"
            color={STATUS_COLORS[params.row.status as keyof typeof STATUS_COLORS] || 'default'}
            sx={{ fontWeight: 500 }}
          />
        </Box>
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.row.priority_display}
            size="small"
            color="default"
          />
        </Box>
      ),
    },
    {
      field: 'assigned_to_name',
      headerName: 'Assigned To',
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.row.assigned_to_name || 'Unassigned'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'created_by_name',
      headerName: 'Assigned By',
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.row.created_by_name || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 140,
      renderCell: (params) => {
        if (!params.row.due_date) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2">—</Typography>
            </Box>
          );
        }

        const dueDate = new Date(params.row.due_date);
        const isOverdue = params.row.is_overdue;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography
              variant="body2"
              sx={{
                color: isOverdue ? 'error.main' : 'text.primary',
                fontWeight: isOverdue ? 600 : 400,
              }}
            >
              {dueDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Assigned',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {getRelativeTime(params.row.created_at)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setActionMenuAnchor({ element: e.currentTarget, task: params.row });
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleRowClick = async (params: any) => {
    setTaskDetailLoading(true);
    setModalOpen(true);
    try {
      // Fetch full task details including description and attachments
      const response = await api.get(`/tasks/${params.row.id}/`, { headers });
      setSelectedTask(response.data);
      setEditedStatus(response.data.status);
      setEditedPriority(response.data.priority);
      setEditedDueDate(response.data.due_date ? response.data.due_date.split('T')[0] : '');
      setEditedTitle(response.data.title);
      setEditedDescription(response.data.description || '');
      setEditedFiles([]);
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      setSelectedTask(params.row); // Fallback to row data
      setEditedStatus(params.row.status);
      setEditedPriority(params.row.priority);
      setEditedDueDate(params.row.due_date ? params.row.due_date.split('T')[0] : '');
      setEditedTitle(params.row.title);
      setEditedDescription(params.row.description || '');
      setEditedFiles([]);
    } finally {
      setTaskDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTask(null);
    setEditedStatus('');
    setEditedPriority('');
    setEditedDueDate('');
    setEditedTitle('');
    setEditedDescription('');
    setEditedFiles([]);
  };

  const handleUpdateTask = async () => {
    if (!selectedTask || !editedTitle.trim()) return;

    setUpdating(true);
    try {
      // Ensure description is a string (could be object from backend or string from editor)
      const descriptionToSend = typeof editedDescription === 'string'
        ? editedDescription
        : JSON.stringify(editedDescription);

      let response: any;

      // Use FormData if there are files to upload
      if (editedFiles.length > 0) {
        const formData = new FormData();
        formData.append('title', editedTitle.trim());
        formData.append('description', descriptionToSend);
        formData.append('status', editedStatus);
        formData.append('priority', editedPriority);

        if (editedDueDate) {
          formData.append('due_date', editedDueDate);
        }

        // Append all new files
        editedFiles.forEach((file) => {
          formData.append('attachment_files', file);
        });

        response = await api.patch(
          `/tasks/${selectedTask.id}/`,
          formData,
          {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        // Use JSON payload when no files
        response = await api.patch(
          `/tasks/${selectedTask.id}/`,
          {
            title: editedTitle.trim(),
            description: descriptionToSend,
            status: editedStatus,
            priority: editedPriority,
            due_date: editedDueDate || null,
          },
          { headers }
        );
      }

      // Refresh the full task data to get updated attachments
      const updatedTaskResponse = await api.get(`/tasks/${selectedTask.id}/`, { headers });
      setSelectedTask(updatedTaskResponse.data);
      setEditedFiles([]);

      // Update the task in the list
      setTasks(tasks.map(task =>
        task.id === selectedTask.id
          ? {
              ...task,
              title: editedTitle,
              status: editedStatus,
              priority: editedPriority,
              due_date: editedDueDate || null,
              status_display: response.data.status_display,
              priority_display: response.data.priority_display
            }
          : task
      ));
    } catch (error) {
      console.error('Failed to update task:', error);
      alert('Failed to update task. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTask) return;

    setDeleteConfirmOpen(false);
    setUpdating(true);
    try {
      await api.delete(`/tasks/${selectedTask.id}/`, { headers });

      // Remove the task from the list
      setTasks(tasks.filter(task => task.id !== selectedTask.id));

      // Close the modal
      handleCloseModal();
    } catch (error) {
      console.error('Failed to delete task:', error);
      alert('Failed to delete task. Please try again.');
      setUpdating(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  const handleViewCase = () => {
    if (selectedTask?.case) {
      navigate(`/cases/${selectedTask.case}?tab=tasks&taskId=${selectedTask.id}`);
    }
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  const handleViewTaskFromMenu = async () => {
    if (!actionMenuAnchor) return;
    const task = actionMenuAnchor.task;
    setActionMenuAnchor(null);

    // Same logic as handleRowClick
    setTaskDetailLoading(true);
    setModalOpen(true);
    try {
      const response = await api.get(`/tasks/${task.id}/`, { headers });
      setSelectedTask(response.data);
      setEditedStatus(response.data.status);
      setEditedPriority(response.data.priority);
      setEditedDueDate(response.data.due_date ? response.data.due_date.split('T')[0] : '');
      setEditedTitle(response.data.title);
      setEditedDescription(response.data.description || '');
      setEditedFiles([]);
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      setSelectedTask(task as TaskDetail);
      setEditedStatus(task.status);
      setEditedPriority(task.priority);
      setEditedDueDate(task.due_date ? task.due_date.split('T')[0] : '');
      setEditedTitle(task.title);
      setEditedDescription(task.description || '');
      setEditedFiles([]);
    } finally {
      setTaskDetailLoading(false);
    }
  };

  const handleViewCaseFromMenu = () => {
    if (!actionMenuAnchor) return;
    const task = actionMenuAnchor.task;
    setActionMenuAnchor(null);
    if (task.case) {
      navigate(`/cases/${task.case}?tab=tasks&taskId=${task.id}`);
    }
  };

  const handleDeleteFromMenu = () => {
    if (!actionMenuAnchor) return;
    const task = actionMenuAnchor.task;
    setActionMenuAnchor(null);

    // Set the task as selected and open delete confirmation
    setSelectedTask(task as TaskDetail);
    setDeleteConfirmOpen(true);
  };

  const handleOpenCreateTaskModal = async () => {
    // Fetch available users for assignment
    try {
      const response = await api.get('/users/', { headers });
      setAvailableUsers(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
    setCreateTaskModalOpen(true);
  };

  const handleCloseCreateTaskModal = () => {
    setCreateTaskModalOpen(false);
    setNewTaskTitle('');
    setNewTaskDescription('');
    setNewTaskDueDate('');
    setNewTaskPriority('medium');
    setNewTaskAssignedTo('');
    setNewTaskFiles([]);
  };

  const handleCreateTask = async () => {
    if (!newTaskTitle.trim()) return;

    try {
      // Ensure description is a string (could be object or string from editor)
      const descriptionToSend = typeof newTaskDescription === 'string'
        ? newTaskDescription
        : JSON.stringify(newTaskDescription);

      // Use FormData if there are files to upload
      if (newTaskFiles.length > 0) {
        const formData = new FormData();
        formData.append('title', newTaskTitle.trim());
        formData.append('description', descriptionToSend);
        formData.append('priority', newTaskPriority);

        if (newTaskDueDate) {
          formData.append('due_date', new Date(newTaskDueDate).toISOString());
        }

        if (newTaskAssignedTo) {
          formData.append('assigned_to', newTaskAssignedTo);
        }

        // Append all files
        newTaskFiles.forEach((file) => {
          formData.append('attachment_files', file);
        });

        await api.post('/tasks/', formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Use JSON payload when no files
        const payload: any = {
          title: newTaskTitle.trim(),
          description: descriptionToSend,
          priority: newTaskPriority,
        };

        if (newTaskDueDate) {
          payload.due_date = new Date(newTaskDueDate).toISOString();
        }

        if (newTaskAssignedTo) {
          payload.assigned_to = newTaskAssignedTo;
        }

        await api.post('/tasks/', payload, { headers });
      }

      handleCloseCreateTaskModal();
      // Refresh task list
      fetchTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      alert('Failed to create task. Please try again.');
    }
  };

  return (
    <Layout>
      {/* Tabs and Search */}
      <Card
          sx={{
            mb: 2,
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
              <Tab label="Assigned to Me" icon={<PersonIcon />} iconPosition="start" />
              <Tab label="Assigned By Me" icon={<TaskIcon />} iconPosition="start" />
            </Tabs>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateTaskModal}
              size="medium"
              sx={{ my: 1 }}
            >
              Create Task
            </Button>
          </Box>
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                size="medium"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="not_completed">Not Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Card>

        {/* Tasks Table */}
        <Card
          sx={{
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          }}
        >
          <DataGrid
            rows={filteredTasks}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            onRowClick={handleRowClick}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            sx={{
              backgroundColor: 'white',
              borderRadius: 0,
              border: '1px solid',
              borderColor: 'divider',
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
                backgroundColor: 'action.hover',
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                paddingTop: '4px',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                py: 0,
                padding: '16px 16px',
                fontSize: '0.9375rem',
              },
              '& .MuiDataGrid-cell:first-of-type': {
                paddingLeft: '24px',
              },
              '& .MuiDataGrid-cell:last-of-type': {
                paddingRight: '24px',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                borderRadius: 0,
                borderBottom: '3px solid #9e9e9e',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#f5f5f5',
                  padding: '16px 16px 12px 16px',
                  minHeight: '64px !important',
                  height: 'auto !important',
                  maxHeight: 'none !important',
                },
                '& .MuiDataGrid-columnHeader:first-of-type': {
                  paddingLeft: '24px',
                },
                '& .MuiDataGrid-columnHeader:last-of-type': {
                  paddingRight: '24px',
                },
                '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                  overflow: 'visible',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'text.primary',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  lineHeight: 1.2,
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                },
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                  padding: '0 4px',
                },
                '& .MuiDataGrid-iconButtonContainer': {
                  '& .MuiIconButton-root': {
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'text.primary',
                    },
                  },
                },
                '& .MuiDataGrid-sortIcon': {
                  color: 'text.secondary',
                },
                '& .MuiDataGrid-menuIcon': {
                  color: 'text.secondary',
                },
              },
              '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid',
                borderTopColor: 'divider',
                backgroundColor: '#fafafa',
              },
            }}
            autoHeight
          />
        </Card>

        {/* Task Detail Modal */}
        <Dialog
          open={modalOpen}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TaskIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Task Details
              </Typography>
            </Box>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            {taskDetailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Typography>Loading...</Typography>
              </Box>
            ) : selectedTask ? (
              <Box>
                {/* Title */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TaskIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Title
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'grey.50',
                      }
                    }}
                  />
                </Box>

                {/* Status, Priority, and Due Date */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Status
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={editedStatus}
                        onChange={(e) => setEditedStatus(e.target.value)}
                        renderValue={(value) => (
                          <Chip
                            label={value === 'pending' ? 'Pending' : value === 'in_progress' ? 'In Progress' : value === 'completed' ? 'Completed' : value === 'blocked' ? 'Blocked' : 'Cancelled'}
                            size="small"
                            color={STATUS_COLORS[value as keyof typeof STATUS_COLORS] || 'default'}
                            sx={{ height: 24 }}
                          />
                        )}
                      >
                        <MenuItem value="pending">
                          <Chip label="Pending" size="small" color="default" />
                        </MenuItem>
                        <MenuItem value="in_progress">
                          <Chip label="In Progress" size="small" color="info" />
                        </MenuItem>
                        <MenuItem value="completed">
                          <Chip label="Completed" size="small" color="success" />
                        </MenuItem>
                        <MenuItem value="blocked">
                          <Chip label="Blocked" size="small" color="error" />
                        </MenuItem>
                        <MenuItem value="cancelled">
                          <Chip label="Cancelled" size="small" color="warning" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Priority
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={editedPriority}
                        onChange={(e) => setEditedPriority(e.target.value)}
                        renderValue={(value) => (
                          <Chip
                            label={value === 'low' ? 'Low' : value === 'medium' ? 'Medium' : 'High'}
                            size="small"
                            color="default"
                            sx={{ height: 24 }}
                          />
                        )}
                      >
                        <MenuItem value="low">
                          <Chip label="Low" size="small" color="default" />
                        </MenuItem>
                        <MenuItem value="medium">
                          <Chip label="Medium" size="small" color="default" />
                        </MenuItem>
                        <MenuItem value="high">
                          <Chip label="High" size="small" color="default" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Due Date
                    </Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={editedDueDate}
                      onChange={(e) => setEditedDueDate(e.target.value)}
                      sx={{ minWidth: 150 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Box>
                </Box>

                {/* Description */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DescriptionIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Description
                    </Typography>
                  </Box>
                  <RichTextEditor
                    initialContent={editedDescription}
                    onChange={(content) => setEditedDescription(content)}
                    placeholder="Provide detailed instructions or context for this task..."
                    minHeight={120}
                    showSaveButton={false}
                  />
                </Box>

                {/* Assigned To */}
                {selectedTask.assigned_to_name && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Assigned To
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ ml: 3.5 }}>
                      {selectedTask.assigned_to_name}
                    </Typography>
                  </Box>
                )}

                {/* Case */}
                {selectedTask.case_name && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CaseIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Case
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ ml: 3.5 }}>
                      {selectedTask.case_name}
                    </Typography>
                  </Box>
                )}

                {/* Existing Attachments */}
                {selectedTask.has_attachments && selectedTask.attachments && selectedTask.attachments.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AttachFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Existing Attachments ({selectedTask.attachments.length})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 3.5 }}>
                      {selectedTask.attachments.map((attachment) => (
                        <Chip
                          key={attachment.id}
                          label={attachment.original_filename}
                          icon={<AttachFileIcon />}
                          onClick={async () => {
                            try {
                              const response = await api.get(attachment.file_url, { headers });
                              const downloadUrl = response.data.url;

                              // For local development, fetch with auth and create blob
                              if (downloadUrl.startsWith('http://localhost') || downloadUrl.startsWith('http://127.0.0.1')) {
                                // Extract the path after /api/
                                const urlPath = downloadUrl.replace('http://localhost:8000/api', '');
                                const fileResponse = await api.get(urlPath, {
                                  headers,
                                  responseType: 'blob'
                                });

                                // Create blob URL and trigger download
                                const blob = new Blob([fileResponse.data]);
                                const blobUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.download = attachment.original_filename;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(blobUrl);
                              } else {
                                // For S3 presigned URLs, direct download
                                const link = document.createElement('a');
                                link.href = downloadUrl;
                                link.download = attachment.original_filename;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            } catch (error) {
                              console.error('Failed to download attachment:', error);
                              alert('Failed to download attachment. Please try again.');
                            }
                          }}
                          clickable
                          size="small"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'primary.contrastText',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Add New Attachments */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <UploadIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Add New Attachments
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 3.5 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      size="small"
                    >
                      Upload Files
                      <input
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setEditedFiles([...editedFiles, ...files]);
                        }}
                        accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                      />
                    </Button>
                    {editedFiles.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          {editedFiles.length} file{editedFiles.length !== 1 ? 's' : ''} to upload:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {editedFiles.map((file, index) => (
                            <Chip
                              key={index}
                              label={file.name}
                              onDelete={() => setEditedFiles(editedFiles.filter((_, i) => i !== index))}
                              size="small"
                              icon={<UploadIcon sx={{ fontSize: 14 }} />}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Created Date */}
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    Created on {new Date(selectedTask.created_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
              </Box>
            ) : null}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteClick}
              startIcon={<DeleteIcon />}
              disabled={updating}
            >
              Delete Task
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {selectedTask?.case && (
                <Button variant="outlined" onClick={handleViewCase} startIcon={<CaseIcon />} disabled={updating}>
                  View Case
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleUpdateTask}
                disabled={updating || !editedTitle.trim() || (
                  editedTitle === selectedTask?.title &&
                  editedDescription === (selectedTask?.description || '') &&
                  editedStatus === selectedTask?.status &&
                  editedPriority === selectedTask?.priority &&
                  editedDueDate === (selectedTask?.due_date ? selectedTask.due_date.split('T')[0] : '') &&
                  editedFiles.length === 0
                )}
              >
                {updating ? 'Updating...' : 'Update Task'}
              </Button>
              <Button variant="outlined" onClick={handleCloseModal} disabled={updating}>
                Close
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleDeleteCancel}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Task</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the task "{selectedTask?.title}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={actionMenuAnchor?.element}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleViewTaskFromMenu}>
            <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
            View Task
          </MenuItem>
          {actionMenuAnchor?.task.case && (
            <MenuItem onClick={handleViewCaseFromMenu}>
              <CaseIcon sx={{ mr: 1, fontSize: 20 }} />
              View Case
            </MenuItem>
          )}
          <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
            Delete Task
          </MenuItem>
        </Menu>

        {/* Create Task Modal */}
        <Dialog
          open={createTaskModalOpen}
          onClose={handleCloseCreateTaskModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TaskIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Create New Task
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 4, pb: 2 }}>
            {/* Title Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 2 }}>
                <TaskIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Title
                </Typography>
                <Chip label="Required" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
              <TextField
                fullWidth
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="Enter a clear, concise task title..."
                variant="outlined"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'grey.50',
                  }
                }}
              />
            </Box>

            {/* Task Description Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <DescriptionIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Description
                </Typography>
              </Box>
              <RichTextEditor
                initialContent={newTaskDescription}
                onChange={(content) => setNewTaskDescription(content)}
                placeholder="Provide detailed instructions or context for this task..."
                minHeight={120}
                showSaveButton={false}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Priority and Assignment Section */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <PriorityIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Priority
                  </Typography>
                </Box>
                <FormControl fullWidth variant="outlined">
                  <Select
                    value={newTaskPriority}
                    onChange={(e) => setNewTaskPriority(e.target.value)}
                    sx={{
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <MenuItem value="low">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                        Low
                      </Box>
                    </MenuItem>
                    <MenuItem value="medium">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                        Medium
                      </Box>
                    </MenuItem>
                    <MenuItem value="high">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                        High
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Assign To
                  </Typography>
                </Box>
                <FormControl fullWidth variant="outlined">
                  <Select
                    value={newTaskAssignedTo}
                    onChange={(e) => setNewTaskAssignedTo(e.target.value)}
                    displayEmpty
                    sx={{
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {availableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Due Date Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CalendarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Due Date
                </Typography>
              </Box>
              <TextField
                fullWidth
                type="date"
                value={newTaskDueDate}
                onChange={(e) => setNewTaskDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                sx={{
                  maxWidth: 300,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'grey.50',
                  }
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Attachments Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <AttachFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Attachments
                </Typography>
              </Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ mb: 2 }}
              >
                Upload Files
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewTaskFiles([...newTaskFiles, ...files]);
                  }}
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                />
              </Button>
              {newTaskFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {newTaskFiles.length} file{newTaskFiles.length !== 1 ? 's' : ''} selected:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {newTaskFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        onDelete={() => setNewTaskFiles(newTaskFiles.filter((_, i) => i !== index))}
                        size="small"
                        icon={<UploadIcon sx={{ fontSize: 14 }} />}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={handleCloseCreateTaskModal} size="large">
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              variant="contained"
              disabled={!newTaskTitle.trim()}
              size="large"
              startIcon={<AddIcon />}
            >
              Create Task
            </Button>
          </DialogActions>
        </Dialog>
    </Layout>
  );
}
